import { useState } from 'react';
import {
  Autocomplete,
  Card,
  Button,
  HStack,
  Text,
  Input,
  VStack,
} from '@ayble-health/dashboard-components';
import { Textarea } from '@chakra-ui/react';
import { useMedicationOptions } from 'api/healthCoach/useMedicationOptions';
import { MedicationOption } from 'api/types';
import useCreateUserMedication from 'api/healthCoach/mutations/useCreateUserMedication';
import { useDashboardPageState } from '../../../hooks/useDashboardPageState';

type Props = {
  onClose: () => void;
};

const AddMedicationForm = ({ onClose }: Props) => {
  const {
    state: { user },
  } = useDashboardPageState();
  const [name, setName] = useState('');
  const [dose, setDose] = useState('');
  const [directions, setDirections] = useState('');
  const [comment, setComment] = useState('');

  const { mutation: createMedicationMutation } = useCreateUserMedication();

  const handleInputValueChange = (details: { inputValue: string }) => {
    setName(details.inputValue);
  };

  const handleValueChange = (details: { items: MedicationOption[] }) => {
    const [selected] = details.items;
    if (selected) setName(selected.name);
  };

  const addMedication = () => {
    if (!user?.id || !name.trim()) return;

    createMedicationMutation.mutate(
      {
        userId: user.id,
        name: name.trim(),
        dosage: dose.trim() || undefined,
        directions: directions.trim() || undefined,
        comment: comment.trim() || undefined,
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  return (
    <Card p={4} borderLeftColor={'seafoam'} borderLeftWidth={3}>
      <VStack gap={4}>
        <Text color={'evergreen'} fontSize={'lg'} fontWeight={'semibold'}>
          Add Medication
        </Text>

        <HStack gap={4}>
          <VStack flex={1} gap={1}>
            <Text color={'evergreen'} fontWeight={'semibold'} fontSize={'xs'}>
              Name
            </Text>
            <Autocomplete<MedicationOption>
              value={name}
              onInputValueChange={handleInputValueChange}
              onValueChange={handleValueChange}
              useOptions={useMedicationOptions}
              itemToString={(item) => item.name}
              itemToValue={(item) => item.id}
            />
          </VStack>
          <VStack w={'224px'} flexShrink={0} gap={1}>
            <HStack gap={1}>
              <Text color={'evergreen'} fontWeight={'semibold'} fontSize={'xs'}>
                Dose
              </Text>
              <Text color={'bluegrey'} fontSize={'xs'}>
                (optional)
              </Text>
            </HStack>
            <Input
              placeholder="e.g. 20 mg daily"
              onChange={(e) => setDose(e.target.value)}
              value={dose}
              size={'xs'}
              fontSize={'12px'}
            />
          </VStack>
        </HStack>

        <HStack gap={4}>
          <VStack flex={1} gap={1}>
            <HStack gap={1}>
              <Text color={'evergreen'} fontWeight={'semibold'} fontSize={'xs'}>
                Instructions
              </Text>
              <Text color={'bluegrey'} fontSize={'xs'}>
                (optional)
              </Text>
            </HStack>
            <Textarea
              placeholder="e.g. Take with food, avoid at bedtime"
              onChange={(e) => setDirections(e.target.value)}
              value={directions}
              size={'xs'}
              fontSize={'12px'}
            />
            <Text color={'bluegrey'} fontSize={'12px'} fontWeight={400} my={1}>
              Shown to the patient alongside the dosage.
            </Text>
          </VStack>
          <VStack flex={1} gap={1}>
            <HStack gap={1}>
              <Text color={'evergreen'} fontWeight={'semibold'} fontSize={'xs'}>
                Notes
              </Text>
              <Text color={'bluegrey'} fontSize={'xs'}>
                (optional)
              </Text>
            </HStack>
            <Textarea
              placeholder="e.g. Context for the care team, reason for the change"
              onChange={(e) => setComment(e.target.value)}
              value={comment}
              size={'xs'}
              fontSize={'12px'}
            />
            <Text color={'bluegrey'} fontSize={'12px'} fontWeight={400} my={1}>
              Internal — not shown to the patient.
            </Text>
          </VStack>
        </HStack>

        <HStack justifyContent={'flex-end'} gap={2}>
          <Button
            borderColor={'evergreen'}
            color={'evergreen'}
            variant={'outline'}
            rounded={'sm'}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            onClick={addMedication}
            disabled={!name.length || createMedicationMutation.isLoading}
          >
            Add
          </Button>
        </HStack>
      </VStack>
    </Card>
  );
};

export default AddMedicationForm;
